import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { areAllEdgesWhite } from '@ff-os/shared-react/dom/images/areAllEdgesWhite';
import { isSupportedByBrowser } from '@ff-os/shared-react/dom/images/image-browser-support';
import { useImageSrc } from '@ff-os/shared-react/hooks/useImageSrc';
import { useMobile } from '@ff-os/shared-react/hooks/useMobile';
import { GiftPdpDispatch, GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { UploadStatus } from '@gift-variable-plugins/photo-shared/PhotoVariableInput';
import { selectPrivateAssetUrl, selectStatusById } from '../_redux';
import { uploadImage } from './helpers/uploadImage';
import { ModalPhotoInput } from './ModalPhotoInput';
import { PhotoInputInline } from './PhotoInputInline';

const _PhotoInputContainer = ({
    variableIds, uploadImage,
    assetUrl, status, onConfirm
}: Props) => {

    const [fileErrors, setFileErrors] = useState<UploadError[]>([]);
    const [hasWhiteEdges, setWhiteEdges] = useState(false);
    const { t } = useTranslation();

    const onChange = async (files: File[]) => {
        try {
            const file = files[0];
            if (isSupportedByBrowser(file)) {
                setWhiteEdges(await areAllEdgesWhite(file));
            }
            uploadImage(file);
        } catch (e) {
            setFileErrors([
                { type: 'file', text: t('image_uploader.error_message') }
            ]);
        }
    };

    useEffect(() => {
        if (status === 'error') {
            setFileErrors([
                { type: 'file', text: t('image_uploader.error_message') }
            ]);
        } else {
            setFileErrors([]);
        }
    }, [status, t]);

    const hasFile = status === 'saving' || status === 'saved';
    const imageSrc = useImageSrc(assetUrl);

    const isMobile = useMobile();
    if (isMobile) {
        return (
            <ModalPhotoInput
                imageSrc={imageSrc}
                variableIds={variableIds}
                fileErrors={fileErrors}
                hasFile={hasFile}
                hasWhiteEdges={hasWhiteEdges}
                onChange={onChange}
                onConfirm={onConfirm} />
        );
    } else {
        return (
            <PhotoInputInline
                imageSrc={imageSrc}
                variableIds={variableIds}
                fileErrors={fileErrors}
                hasFile={hasFile}
                hasWhiteEdges={hasWhiteEdges}
                onChange={onChange} />
        );
    }
};

type Props = {

    variableIds: string[];
    onConfirm?: () => void;

    assetUrl: string | undefined;
    uploadImage: (image: File) => void;
    status: UploadStatus | undefined;
};

const mapStateToProps = (state: GiftPdpState, ownProps: { variableIds: string[] }) => ({
    status: selectStatusById(state, ownProps.variableIds[0]),
    assetUrl: selectPrivateAssetUrl(state, ownProps.variableIds[0])
});

const mapDispatchToProps = (dispatch: GiftPdpDispatch, ownProps: { variableIds: string[] }) => ({
    uploadImage: (image: File) => dispatch(uploadImage(image, ownProps.variableIds[0]))
});

export const PhotoInputContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoInputContainer);